import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { TenantDetail, getTenant, refreshTenant } from '../api/tenantRegistry';

// Shared tenant-detail query (ADR-023). TenantLayout owns the canonical observer;
// the four detail tabs call this same hook and read one cache entry
// (queryKey ['tenant', tenantId]).
//
// THE DEDUPE IS NOT AUTOMATIC — it is the staleTime below that delivers it.
// This comment used to claim react-query deduped the layout and the tab, and
// that claim is what made FD-271 invisible: it reads as verified.
export function useTenantDetailQuery(
  tenantId: string | undefined,
): UseQueryResult<TenantDetail> {
  return useQuery<TenantDetail>({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId!),
    enabled: Boolean(tenantId),
    // WITHOUT THIS THE PAGE FETCHES TWICE PER VISIT. TenantLayout returns a
    // skeleton while loading, so the tab child does not mount until the first
    // fetch RESOLVES; its observer then subscribes to already-stale data
    // (default staleTime is 0) and `refetchOnMount` fires a second request. The
    // two observers never overlap, so there is nothing for react-query to dedupe.
    //
    // Load-bearing, not a cache preference. Note the on-demand re-check below
    // primes the cache via setQueryData, so an operator-triggered refresh still
    // updates every tab immediately regardless of this window.
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Don't retry 404s — the tenant genuinely isn't in the registry.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}

// On-demand re-check (ADR-023 §3). POSTs /v1/tenants/{id}/refresh and primes the
// ['tenant', id] cache with the freshly refreshed detail so every tab updates at
// once — no extra GET round-trip.
export function useRefreshTenant(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<TenantDetail, unknown, void>({
    mutationFn: () => refreshTenant(tenantId!),
    onSuccess: (tenant) => {
      queryClient.setQueryData(['tenant', tenantId], tenant);
      // The list snapshot's freshness/verdict may have moved too.
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
